import { Controller } from "@hotwired/stimulus"

/**
 * Loads a drawer's content the first time it is opened, and follows links inside it, both by fetch
 * rather than by Turbo.
 *
 * Turbo is deliberately not used here, for the same reason drawer_action_controller gives: Flowbite
 * ships a Turbo build that re-runs initFlowbite on `turbo:frame-load`, and a fresh Drawer instance
 * starts with `_visible = false` and sets `aria-hidden="true"`. So a turbo-frame that loads inside
 * an open drawer leaves Flowbite believing the drawer is shut: the backdrop goes, and the next
 * click on the trigger opens nothing.
 *
 *   <div id="drawer-show-1" data-controller="drawer-load" data-drawer-load-url-value="/channels/1">
 *     <div data-drawer-load-target="content">Loading…</div>
 *
 * A link inside the loaded content replaces one region of it:
 *
 *   <a href="/channels/1?after=abc" data-action="click->drawer-load#follow"
 *      data-drawer-load-region-param="[data-feed]">Load more</a>
 */
export default class extends Controller {
  static values = { url: String }
  static targets = ["content"]

  connect() {
    // Flowbite owns the open and shut, and gives no event, so the trigger's own click is the
    // signal. The listener stays on rather than firing once, because a fetch that failed has to be
    // retriable: load() is what decides there is nothing left to do.
    this.trigger = document.querySelector(`[data-drawer-show="${this.element.id}"]`)
    this.onOpen = () => this.load()
    this.trigger?.addEventListener("click", this.onOpen)
  }

  disconnect() {
    this.trigger?.removeEventListener("click", this.onOpen)
  }

  // Loaded content is kept, so reopening the drawer costs nothing. A previous attempt that failed
  // left data-loaded="error", and reopening tries again.
  load() {
    if (this.contentTarget.dataset.loaded === "true") return

    return this.fetchInto(this.urlValue, this.contentTarget)
  }

  /**
   * Adds the next page of a feed to the one on screen, rather than replacing it: the response holds
   * the rows and, if there is another page after this one, the link that fetches it. The clicked
   * link is replaced by that one, or removed when the feed has run out.
   *
   *   <a href="/channels/1?after=abc" data-action="click->drawer-load#more"
   *      data-drawer-load-list-param="[data-feed-list]">Load more</a>
   */
  async more(event) {
    event.preventDefault()
    const link = event.currentTarget
    const list = this.element.querySelector(link.dataset.drawerLoadListParam)
    if (!list) return

    const html = await this.fetchText(link.href)
    // A click that does nothing reads as a broken control. The link stays, so the reader can try
    // again, and the reason sits beside it rather than only in the console.
    if (html === "") return this.sayFailed(link)

    // The link goes, so anything said about a previous attempt at it goes with it.
    link.parentElement?.querySelector("[data-feed-error]")?.remove()

    const page = document.createElement("template")
    page.innerHTML = html
    list.append(...page.content.querySelectorAll("li"))
    const next = page.content.querySelector("a[data-feed-next]")
    next ? link.replaceWith(next) : link.remove()
  }

  follow(event) {
    event.preventDefault()
    const link = event.currentTarget
    const region = this.element.querySelector(link.dataset.drawerLoadRegionParam)
    if (!region) return

    return this.fetchInto(link.href, region)
  }

  // data-loaded says the region has settled, which is what a test waits for. A turbo-frame reports
  // itself complete; a fetch has to say so itself.
  async fetchInto(url, element) {
    const html = await this.fetchText(url)
    if (html === "") {
      element.textContent = "Could not load this. Close the drawer and open it again."
      element.dataset.loaded = "error"
      return
    }

    element.innerHTML = html
    element.dataset.loaded = "true"
  }

  // One message beside the link that failed, replaced rather than repeated when it fails again.
  sayFailed(link) {
    const said = link.nextElementSibling?.matches("[data-feed-error]")
      ? link.nextElementSibling
      : link.insertAdjacentElement("afterend", document.createElement("span"))

    said.dataset.feedError = "true"
    said.className = "ml-2 text-xs text-gray-500 dark:text-gray-400"
    said.textContent = "Could not load more. Try again."
  }

  // An empty string for anything that did not arrive, so each caller decides what to say about it.
  async fetchText(url) {
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(response.status)

      return await response.text()
    } catch (error) {
      return ""
    }
  }
}
